// src/business/navigation/businessNavigation.js
// The Business Portal's navigation map: one source for the sidebar, the topbar
// breadcrumb and page-location lookups. Presentation only — the server remains
// the authority on what each member may open.
import {
  LayoutDashboard,
  Package,
  PlusCircle,
  LineChart,
  Users,
  Shield,
  Activity,
  AlertTriangle,
  ClipboardList,
  BriefcaseBusiness,
  FileText,
  FileCheck,
  Clock,
  Building2,
  Warehouse,
  BoxesIcon,
  DollarSign,
  Truck,
  ShoppingCart,
  CalendarDays,
  Timer,
  Building,
  Settings,
  CalendarOff,
  Receipt,
  Banknote,
  CreditCard,
  Wallet,
  MessageSquare,
  Tag,
  ArrowDownToLine,
  ArrowRightLeft,
  ArrowUpDown,
  Star,
  RotateCcw,
  UserRound,
} from "lucide-react";

/* ─── Navigation structure ───────────────────────────────── */

export const DASHBOARD_LINK = {
  label: "Dashboard",
  to: "/business",
  end: true,
  icon: LayoutDashboard,
};

// Revenue, top products, stock and category figures for the whole business.
// It is general reporting, not logistics, so it sits beside the dashboard.
export const SALES_ANALYTICS_LINK = {
  label: "Sales analytics",
  to: "/business/orders/analytics",
  icon: LineChart,
  requiredPermission: "orders:read",
};

// Customer Service groups the shopper-facing work that already exists. Each
// destination keeps its own page, API and permission; nothing here merges
// conversations, return requests and reviews into one record.
const CUSTOMER_SERVICE_NAV = {
  label: "Customer Service",
  icon: MessageSquare,
  prefix: "/business/messages",
  extraPrefixes: ["/business/orders/returns", "/business/products/reviews"],
  showsUnread: true,
  items: [
    { label: "Inbox", to: "/business/messages", icon: MessageSquare, end: true, showsUnread: true },
    { label: "Return requests", to: "/business/orders/returns", icon: RotateCcw, requiredPermission: "orders:refund", end: true },
    { label: "Feedback", to: "/business/products/reviews", icon: Star, requiredPermission: "products:read", end: true },
  ],
};

export const ADMINISTRATION_NAV = {
  label: "Administration",
  icon: Shield,
  prefix: "/business/administration",
  extraPrefixes: ["/business/activity"],
  items: [
    {
      label: "Roles & Permissions",
      to: "/business/administration/roles",
      icon: Shield,
      requiredPermissions: ["members:read", "access_roles.read.business", "permissions.read.business"],
      end: true,
    },
    {
      label: "Activity Logs",
      to: "/business/administration/activity",
      icon: Activity,
      requiredPermissions: ["activity_logs.read.business", "members:read"],
      end: true,
    },
    {
      label: "Calendar",
      to: "/business/administration/calendar",
      icon: CalendarDays,
      requiredPermissions: ["hr:holidays:read", "hr:holidays:manage"],
      end: true,
    },
    { label: "Business settings", to: "/business/settings", icon: Settings, ownerOnly: true, end: true },
  ],
};

const PROCUREMENT_NAV = {
  label: "Procurement",
  icon: ShoppingCart,
  prefix: "/business/procurement",
  extraPrefixes: ["/business/suppliers", "/business/logistics"],
  items: [
    { label: "Dashboard", to: "/business/procurement/dashboard", icon: LayoutDashboard, requiredPermission: "procurement:read", end: true },
    { label: "Open Work", to: "/business/procurement/open-work", icon: Clock, requiredPermission: "procurement:read", end: true },
    { label: "Suppliers", to: "/business/procurement/suppliers", icon: Building2, requiredPermission: "suppliers:read", end: true },
    { label: "Purchase Requisitions", to: "/business/procurement/requisitions", icon: ClipboardList, requiredPermission: "procurement:read", end: true },
    { label: "Request for Quotations", to: "/business/procurement/rfqs", icon: FileText, requiredPermission: "procurement:read", end: true },
    { label: "Purchase Orders", to: "/business/procurement/orders", icon: FileCheck, requiredPermission: "procurement:read", end: true },
    { label: "Deliveries", to: "/business/procurement/deliveries", icon: Truck, requiredPermission: "logistics:read", end: true },
    { label: "Timeline", to: "/business/procurement/timeline", icon: Clock, requiredPermission: "procurement:read", end: true },
  ],
};

const INVENTORY_NAV = {
  label: "Inventory",
  icon: Warehouse,
  prefix: "/business/inventory",
  items: [
    { label: "Dashboard", to: "/business/inventory/dashboard", icon: LayoutDashboard, requiredPermission: "inventory:read", end: true },
    { label: "Open Work", to: "/business/inventory/open-work", icon: Clock, requiredPermission: "inventory:read", end: true },
    { label: "Stock Overview", to: "/business/inventory", icon: BoxesIcon, requiredPermission: "inventory:read", end: true },
    { label: "Receipts", to: "/business/inventory/receipts", icon: ArrowDownToLine, requiredPermission: "inventory:movements:read", end: true },
    { label: "Movements", to: "/business/inventory/movements", icon: ArrowRightLeft, requiredPermission: "inventory:movements:read", end: true },
    { label: "Adjustments", to: "/business/inventory/adjustments", icon: ArrowUpDown, requiredPermission: "inventory:stock:adjust", end: true },
  ],
};

const MERCHANDISING_NAV = {
  label: "Merchandising",
  icon: Package,
  prefix: "/business/merchandising",
  extraPrefixes: ["/business/products"],
  excludePrefixes: ["/business/products/reviews"],
  items: [
    { label: "Dashboard", to: "/business/merchandising/dashboard", icon: LayoutDashboard, requiredPermission: "products:read", end: true },
    { label: "Open Work", to: "/business/merchandising/open-work", icon: Clock, requiredPermission: "products:read", end: true },
    { label: "Categories & Tags", to: "/business/merchandising/categories", icon: Tag, requiredPermissions: ["categories:read", "product_tags:read", "products:read"], end: true },
    { label: "All Products", to: "/business/products", icon: Package, requiredPermission: "products:read", end: true },
    { label: "Add Product", to: "/business/products/add", icon: PlusCircle, requiredPermission: "products:create", end: true },
  ],
};

const DISPATCH_READ = "delivery.dispatch.read.business";
const RIDERS_READ = "delivery.riders.read.business";
const EXCEPTIONS_MANAGE = "delivery.exceptions.manage.business";
const ASSIGNMENT_PROBLEMS_MANAGE = "delivery.assignment_problems.manage.business";
const CUSTODY_RECEIVE = "delivery.custody.receive.business";
const RETURNS_DISPOSITION = "delivery.returns.disposition.business";
const PROOFS_REVIEW = "delivery.proofs.review.business";
const COD_REMIT = "delivery.cod.remit.business";
const DELIVERY_SETTINGS = "delivery.settings.manage.business";

const DELIVERY_VIEWS = ["trips", "parcels", "riders", "returns"];
const EXCEPTION_VIEWS = ["issues", "exceptions", "reports"];
const onDelivery = ({ pathname }) => pathname === "/business/delivery";

const ORDER_RECORDS = "/business/orders";
const isOrderRecord = ({ pathname }) => pathname === ORDER_RECORDS || pathname.startsWith("/business/orders/record");
// The proof page serves two intents. Arriving with ?show=all is the delivery
// record; anything else is the flagged-proof review.
const isProofArchive = (at) => at.pathname.startsWith("/business/delivery/proofs") && at.params.get("show") === "all";

// Five workspaces, each visible to anyone who can do part of its work. A
// specialist lands on the section their permission covers, so nobody is sent
// to a page the server would refuse. The server remains the authority: these
// rules only decide what the menu offers.
const LOGISTICS_NAV = {
  label: "Logistics",
  icon: Truck,
  prefix: "/business/delivery",
  extraPrefixes: ["/business/orders"],
  excludePrefixes: ["/business/orders/returns", "/business/orders/analytics"],
  items: [
    {
      label: "Overview",
      to: "/business/delivery",
      icon: LayoutDashboard,
      requiredPermissions: [
        DISPATCH_READ, "orders:read", EXCEPTIONS_MANAGE, ASSIGNMENT_PROBLEMS_MANAGE, CUSTODY_RECEIVE,
        RETURNS_DISPOSITION, RIDERS_READ, PROOFS_REVIEW, COD_REMIT, DELIVERY_SETTINGS,
      ],
      match: (at) => onDelivery(at) && !at.params.get("trip")
        && ![...DELIVERY_VIEWS, ...EXCEPTION_VIEWS].includes(at.params.get("view") || ""),
    },
    {
      label: "Fulfillment",
      to: "/business/orders/fulfillment",
      icon: ClipboardList,
      requiredPermission: "orders:read",
      match: (at) => at.pathname.startsWith("/business/orders")
        && !isOrderRecord(at)
        && !at.pathname.startsWith("/business/orders/returns")
        && !at.pathname.startsWith("/business/orders/analytics")
        && !at.pathname.startsWith("/business/orders/custody-reviews"),
    },
    {
      // Find and read any order, whatever its state. Fulfillment is where the
      // work on open orders is done.
      label: "Order records",
      to: ORDER_RECORDS,
      icon: FileText,
      requiredPermission: "orders:read",
      match: isOrderRecord,
    },
    {
      label: "Delivery",
      icon: Truck,
      requiredPermissions: [DISPATCH_READ, RIDERS_READ, CUSTODY_RECEIVE, RETURNS_DISPOSITION, COD_REMIT],
      to: (can) => {
        if (can(DISPATCH_READ)) return "/business/delivery?view=trips";
        if (can(RIDERS_READ)) return "/business/delivery?view=riders";
        if (can(CUSTODY_RECEIVE) || can(RETURNS_DISPOSITION)) return "/business/delivery?view=returns";
        return "/business/delivery/settlement";
      },
      match: (at) => at.pathname.startsWith("/business/delivery/settlement")
        || isProofArchive(at)
        || (onDelivery(at) && (Boolean(at.params.get("trip")) || DELIVERY_VIEWS.includes(at.params.get("view") || ""))),
    },
    {
      label: "Exceptions",
      icon: AlertTriangle,
      requiredPermissions: [EXCEPTIONS_MANAGE, ASSIGNMENT_PROBLEMS_MANAGE, PROOFS_REVIEW],
      to: (can) => (can(EXCEPTIONS_MANAGE) || can(ASSIGNMENT_PROBLEMS_MANAGE)
        ? "/business/delivery?view=issues"
        : "/business/delivery/proofs"),
      match: (at) => (at.pathname.startsWith("/business/delivery/proofs") && !isProofArchive(at))
        || at.pathname.startsWith("/business/orders/custody-reviews")
        || (onDelivery(at) && !at.params.get("trip") && EXCEPTION_VIEWS.includes(at.params.get("view") || "")),
    },
  ],
};

const FINANCE_NAV = {
  label: "Finance",
  icon: DollarSign,
  prefix: "/business/finance",
  items: [
    { label: "Dashboard", to: "/business/finance/dashboard", icon: LayoutDashboard, requiredPermission: "finance:read", end: true },
    { label: "Open Work", to: "/business/finance/open-work", icon: Clock, requiredPermission: "finance:read", end: true },
    { label: "Approvals", to: "/business/finance/approvals", icon: DollarSign, requiredPermission: "finance:approve", end: true },
    { label: "Payables", to: "/business/finance/payables", icon: Wallet, requiredPermission: "finance:read", end: true },
    { label: "Disbursements", to: "/business/finance/disbursements", icon: Banknote, requiredPermission: "finance:read", end: true },
    { label: "Receivables", to: "/business/finance/receivables", icon: CreditCard, requiredPermission: "finance:read", end: true },
    { label: "Collections", to: "/business/finance/collections", icon: Receipt, requiredPermission: "finance:read", end: true },
    { label: "COD Custody", to: "/business/finance/cod", icon: Banknote, requiredPermission: "delivery.cod.read.business", end: true },
    { label: "Pay presets", to: "/business/finance/salaries", icon: Wallet, requiredPermission: "finance:read", end: true },
    // Not `end`: To pay and History are sub-routes of the same workspace.
    { label: "Payroll", to: "/business/finance/payroll", icon: Banknote, requiredPermission: "finance:read" },
    { label: "Analytics", to: "/business/finance/analytics", icon: LineChart, requiredPermission: "finance:read", end: true },
  ],
};

const HR_NAV = {
  label: "HR",
  icon: Users,
  prefix: "/business/hr",
  items: [
    // My HR's sections and My Payslips all belong to it, so My HR stays selected on each.
    { label: "My HR", to: "/business/hr/me", icon: UserRound, match: ({ pathname }) => /^\/business\/hr\/(me|my-payslips)(\/.*)?$/.test(pathname) },
    { label: "Dashboard", to: "/business/hr/dashboard", icon: LayoutDashboard, requiredPermission: "hr:employees:read", end: true },
    { label: "Open Work", to: "/business/hr/open-work", icon: Clock, requiredPermission: "hr:employees:read", end: true },
    { label: "Recruitment", to: "/business/hr/recruitment", icon: BriefcaseBusiness, requiredPermissions: ["hr:employees:read", "members:invite"], end: true },
    { label: "Employees", to: "/business/hr/employees", icon: Users, requiredPermission: "hr:employees:read" },
    { label: "Departments", to: "/business/hr/departments", icon: Building, requiredPermissions: ["hr:departments:read", "departments.read.business"], end: true },
    { label: "Attendance", to: "/business/hr/attendance", icon: CalendarDays, requiredPermission: "hr:attendance:read", end: true },
    { label: "Shifts", to: "/business/hr/shifts", icon: Timer, requiredPermissions: ["hr:shifts:read", "hr:read", "hr:shifts:manage", "hr:manage"], end: true },
    { label: "Leave", to: "/business/hr/leave", icon: CalendarOff, requiredPermissions: ["hr:leave_types:read", "hr:leave_balances:read", "hr:leave_requests:read", "hr:leaves:read", "hr:leave_requests:review"], end: true },
    { label: "Deductions", to: "/business/hr/deductions", icon: Receipt, requiredPermission: "hr:deductions:read" },
    { label: "Pay presets", to: "/business/hr/pay-presets", icon: Wallet, requiredPermissions: ["hr:compensation:manage", "hr:compensation:read"], end: true },
    { label: "Payroll", to: "/business/hr/payroll", icon: Banknote, requiredPermission: "hr:payroll:read" },
    { label: "Analytics", to: "/business/hr/analytics", icon: LineChart, requiredPermission: "hr:employees:read", end: true },
    { label: "Settings", to: "/business/hr/settings", icon: Settings, requiredPermission: "hr:settings:read" },
  ],
};

export const NAV_GROUPS = [
  CUSTOMER_SERVICE_NAV,
  LOGISTICS_NAV,
  PROCUREMENT_NAV,
  MERCHANDISING_NAV,
  INVENTORY_NAV,
  FINANCE_NAV,
  HR_NAV,
  ADMINISTRATION_NAV,
];

// Presentation routing only: the rider API remains the eligibility authority.
export function businessRiderNavigation(auth = {}) {
  const permissions = auth.permissions || [];
  const canEnter = permissions.includes("delivery.assignments.read.self");
  const selfService = new Set(["attendance:clock", "hr:leaves:request"]);
  const managementPermissions = NAV_GROUPS.flatMap((group) => group.items)
    .flatMap((item) => item.requiredPermissions || [item.requiredPermission])
    .filter((permission) => permission && !permission.endsWith(".self") && !selfService.has(permission));
  const managesBusiness = ["OWNER", "SUPER_OWNER", "ADMIN"].includes(String(auth.role || "").toUpperCase())
    || managementPermissions.some((permission) => permissions.includes(permission));
  return { canEnter, riderOnly: canEnter && !managesBusiness };
}

/* ─── Access and matching (shared by the sidebar and the breadcrumb) ──── */

const OWNER_LIKE_ROLES = ["OWNER", "SUPER_OWNER", "ADMIN"];

export function navigationAccess(auth = {}) {
  const role = String(auth.role || auth.user?.role || "").toUpperCase();
  const permissions = auth.permissions || [];
  const isOwnerLike = OWNER_LIKE_ROLES.includes(role);
  const can = (permission) => isOwnerLike || permissions.includes(permission);
  const canSee = (item) => {
    if (item.ownerOnly) return role === "OWNER";
    if (isOwnerLike) return true;
    const required = Array.isArray(item.requiredPermissions)
      ? item.requiredPermissions
      : item.requiredPermission
        ? [item.requiredPermission]
        : [];
    return required.length === 0 || required.some((permission) => permissions.includes(permission));
  };
  return { role, can, canSee };
}

export const resolveItemTo = (item, can) => (typeof item.to === "function" ? item.to(can) : item.to);

export function isGroupActive(group, pathname) {
  // Delivery setup is a tab of Business settings, so every settings tab
  // belongs to Administration.
  if (pathname === "/business/settings") return group === ADMINISTRATION_NAV;
  if ((group.excludePrefixes || []).some((prefix) => pathname.startsWith(prefix))) return false;
  if (pathname.startsWith(group.prefix)) return true;
  return (group.extraPrefixes || []).some((prefix) => pathname.startsWith(prefix));
}

export function isItemActive(item, to, { pathname, search = "" }) {
  if (item.match) return item.match({ pathname, params: new URLSearchParams(search) });
  return item.end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
}

/* ─── Breadcrumb ─────────────────────────────────────────────────────── */

// Pages reached from a workspace but not listed in it (detail pages and older
// aliases). Used only where no navigation entry names the page.
const PAGE_TITLES = [
  [/^\/business\/messages(\/|$)/, "Inbox"],
  [/^\/business\/activity$/, "Activity Logs"],
  [/^\/business\/products\/[^/]+$/, "Product details"],
  [/^\/business\/orders\/dashboard$/, "Orders overview"],
  [/^\/business\/orders\/open-work$/, "Orders open work"],
  [/^\/business\/orders\/shipping$/, "Shipping"],
  [/^\/business\/orders\/custody-reviews(\/|$)/, "Custody reviews"],
  [/^\/business\/orders\/record\/[^/]+$/, "Order record"],
  [/^\/business\/orders\/fulfillment\/[^/]+\/pack$/, "Pack"],
  [/^\/business\/orders\/fulfillment\/[^/]+\/label\/print$/, "Print label"],
  [/^\/business\/orders\/fulfillment\/[^/]+\/label$/, "Label"],
  [/^\/business\/orders\/fulfillment\/[^/]+\/handoff$/, "Handoff"],
  [/^\/business\/orders\/fulfillment\/[^/]+$/, "Fulfillment task"],
  [/^\/business\/analytics$/, "Analytics"],
  [/^\/business\/delivery\/proofs(\/|$)/, "Delivery proofs"],
  [/^\/business\/delivery\/settlement(\/|$)/, "Settlement desk"],
  [/^\/business\/suppliers(\/|$)/, "Suppliers"],
  [/^\/business\/logistics(\/|$)/, "Deliveries"],
  [/^\/business\/notifications(\/|$)/, "Notifications"],
  [/^\/business\/hr\/invitations(\/|$)/, "Invitations"],
  [/^\/business\/hr\/recruitment\/applicants\/[^/]+$/, "Applicant workspace"],
  [/^\/business\/hr\/recruitment\/applicants$/, "Applicants"],
  [/^\/business\/hr\/onboarding(-queue)?(\/|$)/, "Onboarding"],
  [/^\/business\/hr\/employees\/[^/]+\/separation$/, "Separation"],
  [/^\/business\/hr\/employees\/[^/]+(\/(attendance|leave|pay|government|payslips|deductions|financials|history|activity))?$/, "Employee"],
  [/^\/business\/hr\/roles(\/|$)/, "Roles & Permissions"],
  [/^\/business\/hr\/activity(\/|$)/, "Activity Logs"],
  [/^\/business\/hr\/payroll\/periods\/[^/]+$/, "Payroll period"],
  [/^\/business\/hr\/payroll\/payslips\/[^/]+$/, "Payslip"],
  [/^\/business\/hr\/payroll\/schedules\/[^/]+(\/(employees|periods|history|settings))?$/, "Pay schedule"],
  [/^\/business\/hr\/payroll\/schedules$/, "Pay schedules"],
  [/^\/business\/hr\/my-payslips\/[^/]+$/, "Payslip"],
  [/^\/business\/hr\/my-payslips(\/|$)/, "My Payslips"],
  [/^\/business\/hr\/records(\/|$)/, "Records"],
  [/^\/business\/employees(\/|$)/, "Staff & Roles"],
];

function titleize(slug = "") {
  return slug
    .replace(/[-_]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const pathOf = (to = "") => to.split("?")[0];

/**
 * businessBreadcrumb — Module › Page (› detail) for the current location,
 * built from the same map the sidebar draws. The last crumb is the page and
 * carries no link. Labels come from the navigation entries, never invented.
 */
export function businessBreadcrumb({ pathname, search = "" }, access, { riderOnly = false } = {}) {
  const path = pathname.replace(/\/+$/, "") || "/business";
  if (path === "/business" || path === "/business/dashboard") {
    return [{ label: riderOnly ? "Work home" : DASHBOARD_LINK.label }];
  }
  if (path === SALES_ANALYTICS_LINK.to) return [{ label: SALES_ANALYTICS_LINK.label }];

  const detail = PAGE_TITLES.find(([pattern]) => pattern.test(path))?.[1];
  const group = NAV_GROUPS.find((candidate) => isGroupActive(candidate, path));
  if (!group) return [{ label: detail || titleize(path.split("/").pop()) }];

  const visible = group.items.filter(access.canSee);
  const crumbs = [{ label: group.label, to: visible.length ? resolveItemTo(visible[0], access.can) : undefined }];
  const item = group.items.find((candidate) => isItemActive(candidate, resolveItemTo(candidate, access.can), { pathname: path, search }));

  if (item) {
    const itemTo = resolveItemTo(item, access.can);
    const deeper = detail && detail !== item.label && path !== pathOf(itemTo);
    crumbs.push({ label: item.label, to: deeper ? itemTo : undefined });
    if (deeper) crumbs.push({ label: detail });
  } else if (detail) {
    crumbs.push({ label: detail });
  }
  return crumbs;
}
